import {
  AVAILABLE_PANELISTS_CAP,
  DEFAULT_RESPONSE_COUNT,
  QUESTION_TYPES,
} from '@api/instant_survey';
import { isEUDomain } from '@helpers';
import { formatData } from '@utilities';

/**
 * format available panelists
 * @param  {Object} options.group
 * @param  {Number} options.group.quotaLimit
 * @param  {Number} options.group.validationUsers
 * @return {String}
 */
export const formatAvailablePanelists = ({ quotaLimit, validationUsers }) => {
  const cappedAvailablePanelists = Math.min(AVAILABLE_PANELISTS_CAP, 15 * quotaLimit);
  const displayedPanelistCount = Math.min(validationUsers, cappedAvailablePanelists);

  return `${formatData({ format: '0,0', value: displayedPanelistCount })}${
    validationUsers >= cappedAvailablePanelists ? '+' : ''
  }`;
};

/**
 * get question cost
 * @param {Object} question
 * @param {Boolean} question._delete
 * @param {Object} question.properties
 * @param {String} question.type
 * @returns {Number} integer cost to compare against questionnaire pricing
 */
export const getQuestionCost = ({ _delete, properties, subQuestions, type }) => {
  if (_delete) return 0;

  switch (type) {
    case QUESTION_TYPES.INSTRUCTIONS.value:
      return 0;
    case QUESTION_TYPES['TABLE-RADIO'].value:
      return Math.ceil(subQuestions?.length / 4);
    case QUESTION_TYPES.MAX_DIFF.value:
      return Math.ceil(properties?.numberOfSets / 5);
    default:
      return 1;
  }
};

/**
 * get questions cost - reducer shortcut for get question cost
 * @param {Object[]} questions
 * @returns {Number}
 */
export const getQuestionsCost = (questions = []) =>
  questions.reduce((count, question) => count + getQuestionCost(question), 0);

/**
 * get current questionnaire price
 * @param  {Object[]} options.questionnairePricing
 * @param  {Object} options.survey
 * @return {Object}
 */
export const getCurrentQuestionnairePrice = ({ questionnairePricing, survey }) => {
  const totalCount = getQuestionsCost(survey.questions);
  return questionnairePricing?.find((p) => p.questions === totalCount);
};

/**
 * get current sample price
 * @param  {Object[]} options.samplePricing
 * @param  {Object} options.survey
 * @return {Object}
 */
export const getCurrentSamplePrice = ({ samplePricing, survey }) => {
  const activeGroups = survey.groups.filter(({ _delete }) => !_delete);

  return samplePricing?.find(
    ({ quotaGroups, responses }) =>
      quotaGroups === activeGroups.length &&
      responses === activeGroups.reduce((acc, { quotaLimit }) => acc + quotaLimit, 0)
  );
};

/**
 * get quota limit
 * @param {String} surveyTypeId
 * @return {Number}
 */
export const getQuotaLimit = (surveyTypeId) => {
  if (isEUDomain()) return DEFAULT_RESPONSE_COUNT;
  const surveyTypesWithHigherDefault = [
    'standard-v3-instant-survey-type',
    'monadic-concept-test-v3-instant-survey-type',
    'concept-test-v3-instant-survey-type',
    'turf-v3-instant-survey-type',
  ];
  return surveyTypesWithHigherDefault.includes(surveyTypeId) ? 300 : DEFAULT_RESPONSE_COUNT;
};

/** get modal config based on survey type
 * @param {String} surveyType
 * @return {Object|null}
 */
export const getModalConfig = ({ surveyType }) => {
  switch (surveyType) {
    case 'monadic':
      return {
        description: `The Monadic Concept Test is used to evaluate multiple concepts—such as product designs,
          claims, or ads—by showing each respondent only one concept. This setup enables clean comparisons
          across distinct groups without bias from seeing multiple options.`,
        link: 'https://help.insights.numerator.com/en/articles/12141196-in-platform-survey-types-about-the-monadic-concept-test-survey',
        steps: [
          'Add concept images or names to each quota group when selecting panelists.',
          'Tag relevant questions to dynamically display each group’s assigned concept.',
          'The final report will compare results by concept, based on quota group exposure.',
        ],
        title: 'About the Monadic Concept Test Survey',
      };

    case 'turf':
      return {
        description: `TURF (Total Unduplicated Reach and Frequency) helps identify the optimal combination
        of options—such as features, flavors, or messages—that reaches the largest number of unique respondents.`,
        link: 'https://help.insights.numerator.com/en/articles/12333486-in-platform-survey-types-about-turf',
        steps: [
          'Select your desired panelist criteria.',
          'Add at least one TURF question during questionnaire creation.',
          'The final report will include a TURF analysis showing which combinations maximize reach.',
        ],
        title: 'About the TURF Survey',
      };

    default:
      return null;
  }
};
